import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Upload, 
  Search, 
  BarChart3, 
  Shield, 
  Zap, 
  ArrowRight,
  Check,
  ChevronRight,
  Globe,
  TrendingUp,
  FileText,
  Menu,
  X,
  Sparkles,
  LineChart,
  Package
} from 'lucide-react';

const Landing = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "AI-Powered Classification",
      description: "Intelligent HS code suggestions powered by Gemini AI for single products, plus lightning-fast bulk catalog processing.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Smart Sourcing Optimizer",
      description: "Find the top 3 best countries to source from based on tariffs, trade agreements, lead times, and quality tiers.",
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "Origin & Preference Engine",
      description: "Validate country of origin, check FTA eligibility, and model scenarios when manufacturing locations change.",
      gradient: "from-violet-500 to-purple-500"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Classification Governance",
      description: "Version-controlled audit trail, inconsistency detection, confidence tracking, and tariff schedule impact alerts.",
      gradient: "from-amber-500 to-orange-500"
    },
    {
      icon: <LineChart className="w-6 h-6" />,
      title: "Visual Analytics",
      description: "Executive dashboards showing recoverable margin, optimization opportunities, and confidence distributions.",
      gradient: "from-indigo-500 to-blue-500"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Landed Cost Optimization",
      description: "Calculate effective tariff rates including Section 301 duties, FTA benefits, and identify savings up to 25%.",
      gradient: "from-cyan-500 to-teal-500"
    }
  ];

  const pricingTiers = [
    {
      name: "Free",
      price: "$0",
      description: "Try before you buy",
      features: [
        "100 credits/month",
        "~100 Gemini OR ~3 GPT-4o OR ~2 Claude",
        "20 bulk upload products (one-time)",
        "3 sourcing optimizations/month",
        "1 origin validation/month"
      ],
      cta: "Get Started",
      highlighted: false
    },
    {
      name: "Starter",
      price: "$20",
      period: "/mo",
      description: "For small importers",
      features: [
        "1,000 credits/month",
        "~1,000 Gemini OR ~33 GPT-4o analyses",
        "500 bulk upload products/month",
        "50 sourcing optimizations/month",
        "20 origin validations/month",
        "100 ERP Smart Import products/month",
        "Full dashboard access",
        "Export reports"
      ],
      cta: "Start Free Trial",
      highlighted: false
    },
    {
      name: "Growth",
      price: "$99",
      period: "/mo",
      description: "For scaling brands",
      features: [
        "10,000 credits/month",
        "~10,000 Gemini OR ~333 GPT-4o analyses",
        "5,000 bulk upload products/month",
        "Unlimited sourcing optimizer",
        "Unlimited origin engine",
        "1,000 ERP Smart Import products/month",
        "Priority support",
        "Full audit trail"
      ],
      cta: "Start Free Trial",
      highlighted: true
    },
    {
      name: "Scale",
      price: "$499",
      period: "/mo",
      description: "For mid-market importers",
      features: [
        "50,000 credits/month",
        "25,000 bulk upload products/month",
        "5,000 ERP Smart Import products/month",
        "15 included seats",
        "Unlimited sourcing & origin",
        "Dedicated support",
        "Multi-entity ready"
      ],
      cta: "Start Free Trial",
      highlighted: false
    },
  ];

  const enterpriseTiers = [
    {
      name: "Enterprise Starter",
      price: "€60,000 – €90,000",
      period: "/year",
      tagline: "Enterprise-grade visibility into tariff exposure",
      description: "For organizations beginning to scale tariff analysis across teams and systems, with structured data ingestion and financial impact visibility.",
      features: [
        "Bulk HS classification analysis",
        "Master data ingestion (ERP exports such as SAP)",
        "Transactional data analysis (moderate scale)",
        "Misclassification detection",
        "Financial impact estimation (duties, penalties, exposure)",
        "Exportable reports for internal sharing",
        "Limited historical data analysis",
        "Basic AI-assisted insights"
      ],
      badge: null,
    },
    {
      name: "Enterprise Monitor",
      price: "€100,000 – €150,000",
      period: "/year",
      tagline: "Continuous tariff intelligence across your operations",
      description: "For organizations requiring ongoing monitoring, deeper analysis, and proactive visibility into tariff exposure and financial impact.",
      features: [
        "Everything in Enterprise Starter, plus:",
        "Continuous tariff change monitoring",
        "Regulatory updates & classification risk tracking",
        "Real-time alerts and notifications",
        "Full historical transaction scanning at scale",
        "Ongoing financial impact tracking",
        "AI analyst embedded across workflows",
        "Landed cost calculator",
        "Baseline sourcing optimization"
      ],
      badge: "Most Popular",
    },
    {
      name: "Enterprise Scale",
      price: "€200,000 – €350,000+",
      period: "/year",
      tagline: "Global trade intelligence across your entire organization",
      description: "For multinational organizations requiring full-scale deployment, integration, governance, and advanced optimization across global operations.",
      features: [
        "Everything in Enterprise Monitor, plus:",
        "Multi-country and multi-entity coverage",
        "High-volume / near-unlimited data processing",
        "Full transactional & master data at global scale",
        "ERP integrations (SAP and other systems)",
        "API access for real-time tariff intelligence",
        "Advanced sourcing optimization & scenario modeling",
        "Duty recovery and refund analysis",
        "Full audit trails & classification tracking",
        "Role-based access & cross-team oversight",
        "Dedicated onboarding & implementation support"
      ],
      badge: "For Global Enterprises",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#06B6D4] flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="font-outfit font-bold text-lg text-slate-900">Decoded</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Features</a>
              <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Pricing</a>
              <Link to="/resources" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Resources</Link>
              <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Login</Link>
              <Link to="/signup">
                <Button size="sm" className="bg-[#0B3D91] hover:bg-[#082A66]" data-testid="nav-get-started-btn">
                  Get Started
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 rounded-xl hover:bg-slate-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-slate-100 py-4 px-6">
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-blue-600 py-2">Features</a>
              <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-blue-600 py-2">Pricing</a>
              <Link to="/resources" className="text-sm font-medium text-slate-600 hover:text-blue-600 py-2">Resources</Link>
              <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-blue-600 py-2">Login</Link>
              <Link to="/signup">
                <Button size="sm" className="bg-[#0B3D91] hover:bg-[#082A66] w-full">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 px-6 relative">
        {/* Background elements */}
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#2563EB]/10 to-[#06B6D4]/10 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-[#06B6D4]/10 to-[#2563EB]/10 rounded-full blur-3xl -z-10" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Sparkles className="w-4 h-4" />
                AI-Powered Tariff Classification
              </div>
              
              <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
                Turn Tariff Complexity into{' '}
                <span className="text-gradient">Competitive Advantage</span>
              </h1>
              
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                AI-powered Tariff Classification Engine with Landed Cost Optimization. Classify, source, and optimize your global trade operations in minutes.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/signup">
                  <Button 
                    size="lg"
                    className="bg-[#0B3D91] hover:bg-[#082A66] text-base px-6"
                    data-testid="hero-get-started-btn"
                  >
                    Start Free Analysis
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-slate-200 hover:border-blue-200 hover:bg-blue-50/40 text-base px-6"
                    data-testid="hero-learn-more-btn"
                  >
                    See How It Works
                  </Button>
                </a>
              </div>

              {/* Trust indicators */}
              <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#10B981]/10 flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#10B981]" />
                  </div>
                  No credit card required
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#10B981]/10 flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#10B981]" />
                  </div>
                  10 free AI analyses
                </div>
              </div>
            </div>

            {/* Right - Hero Visual */}
            <div className="relative animate-fade-in-up stagger-2">
              <div className="relative">
                {/* Main card */}
                <div className="card-glass p-6 rounded-3xl">
                  <img 
                    src="https://images.unsplash.com/photo-1758900348926-cd1ccbc15fef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBjb250YWluZXIlMjBzaGlwJTIwb2NlYW4lMjBibHVlJTIwc2VhfGVufDB8fHx8MTc3MDgxNjQ3M3ww&ixlib=rb-4.1.0&q=85"
                    alt="Global Trade"
                    className="w-full h-64 object-cover rounded-2xl"
                  />
                </div>
                
                {/* Floating stats card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-slate-100 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#10B981] to-[#06B6D4] flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-2xl font-outfit font-bold text-slate-900">$47,230</div>
                      <div className="text-sm text-slate-500">Avg. Annual Savings</div>
                    </div>
                  </div>
                </div>
                
                {/* Floating badge */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-[#2563EB] to-[#06B6D4] text-white rounded-full px-5 py-2.5 text-sm font-semibold shadow-lg animate-pulse-glow">
                  17% Optimization Rate
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 px-6 bg-gradient-to-b from-white to-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Zap className="w-4 h-4" />
              Features
            </div>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-slate-900 mb-4">
              Everything you need to{' '}
              <span className="text-gradient">optimize</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Powerful AI-driven tools to identify savings, ensure compliance, and streamline your import operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="card-modern p-6 group"
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32 px-6 bg-white relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-br from-[#2563EB]/5 to-[#06B6D4]/5 rounded-full blur-3xl -z-10" />
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#06B6D4]/10 text-[#06B6D4] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Package className="w-4 h-4" />
              How It Works
            </div>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-slate-900 mb-4">
              Three simple steps
            </h2>
            <p className="text-lg text-slate-600">
              From upload to insights in minutes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Upload Catalog",
                description: "Drop your CSV or Excel file with product details and optional financial data.",
                icon: <Upload className="w-8 h-8" />,
                gradient: "from-[#2563EB] to-[#60A5FA]"
              },
              {
                step: "02",
                title: "AI Analysis",
                description: "Our AI processes each SKU, suggesting optimal codes and calculating savings.",
                icon: <Sparkles className="w-8 h-8" />,
                gradient: "from-[#06B6D4] to-[#2DD4BF]"
              },
              {
                step: "03",
                title: "Review & Export",
                description: "Explore your dashboard, drill into high-impact items, and export reports.",
                icon: <BarChart3 className="w-8 h-8" />,
                gradient: "from-[#6366F1] to-[#A5B4FC]"
              }
            ].map((item, index) => (
              <div key={index} className="relative group">
                <div className="card-modern p-8">
                  <div className="text-6xl font-outfit font-bold text-slate-100 mb-4">{item.step}</div>
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    {item.icon}
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-slate-200" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-32 px-6 bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#6366F1]/10 text-[#6366F1] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Zap className="w-4 h-4" />
              Pricing
            </div>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-slate-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-slate-600">
              Start free and scale as your catalog grows
            </p>
          </div>

          {/* Self-serve tiers */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto mb-20">
            {pricingTiers.map((tier, index) => (
              <div 
                key={index}
                className={`rounded-3xl p-6 transition-all duration-300 flex flex-col ${
                  tier.highlighted 
                    ? 'bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white shadow-2xl shadow-blue-500/25 scale-105 lg:scale-110' 
                    : 'card-modern'
                }`}
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
              >
                {tier.highlighted && (
                  <div className="inline-flex items-center gap-1 bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-4 backdrop-blur-sm self-start">
                    <Sparkles className="w-3 h-3" />
                    MOST POPULAR
                  </div>
                )}
                <h3 className={`font-outfit text-xl font-semibold mb-2 ${tier.highlighted ? 'text-white' : 'text-slate-900'}`}>
                  {tier.name}
                </h3>
                <div className="mb-2">
                  <span className={`text-4xl font-outfit font-bold ${tier.highlighted ? 'text-white' : 'text-slate-900'}`}>
                    {tier.price}
                  </span>
                  {tier.period && (
                    <span className={tier.highlighted ? 'text-blue-200' : 'text-slate-500'}>
                      {tier.period}
                    </span>
                  )}
                </div>
                <p className={`text-sm mb-6 ${tier.highlighted ? 'text-blue-200' : 'text-slate-500'}`}>
                  {tier.description}
                </p>
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${tier.highlighted ? 'bg-white/20' : 'bg-[#10B981]/10'}`}>
                        <Check className={`w-3 h-3 ${tier.highlighted ? 'text-white' : 'text-[#10B981]'}`} />
                      </div>
                      <span className={`text-sm ${tier.highlighted ? 'text-blue-100' : 'text-slate-600'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup">
                  <Button 
                    className={`w-full rounded-full py-6 font-semibold transition-all duration-300 mt-auto ${
                      tier.highlighted 
                        ? 'bg-white text-blue-600 hover:bg-blue-50 hover:scale-[1.02]' 
                        : 'bg-[#0B3D91] hover:bg-[#082A66] text-white'
                    }`}
                    data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                  >
                    {tier.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          {/* Enterprise Plans */}
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-slate-900/5 text-slate-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <Shield className="w-4 h-4" />
                Enterprise Plans
              </div>
              <h3 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                Built for organizations at scale
              </h3>
              <p className="text-base text-slate-600 max-w-3xl mx-auto">
                Designed for organizations requiring increasing levels of visibility, control, and optimization of tariff exposure — from initial enterprise deployment to full global trade intelligence.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {enterpriseTiers.map((tier, index) => {
                const isPopular = tier.badge === 'Most Popular';
                return (
                  <div 
                    key={index}
                    className={`rounded-3xl p-6 flex flex-col transition-all duration-300 ${
                      isPopular 
                        ? 'bg-slate-900 text-white shadow-2xl shadow-slate-900/30 scale-[1.03]' 
                        : 'bg-white border-2 border-slate-200 hover:border-slate-300 shadow-sm'
                    }`}
                    data-testid={`enterprise-tier-${index}`}
                  >
                    {tier.badge && (
                      <div className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 self-start ${
                        isPopular ? 'bg-white/15 text-white backdrop-blur-sm' : 'bg-blue-50 text-blue-700'
                      }`}>
                        {isPopular && <Sparkles className="w-3 h-3" />}
                        {tier.badge}
                      </div>
                    )}
                    <h3 className={`font-outfit text-xl font-bold mb-1 ${isPopular ? 'text-white' : 'text-slate-900'}`}>
                      {tier.name}
                    </h3>
                    <p className={`text-sm mb-3 ${isPopular ? 'text-slate-300' : 'text-slate-500'}`}>
                      {tier.tagline}
                    </p>
                    <div className="mb-3">
                      <span className={`text-2xl font-outfit font-bold ${isPopular ? 'text-white' : 'text-slate-900'}`}>
                        {tier.price}
                      </span>
                      <span className={`text-sm ${isPopular ? 'text-slate-400' : 'text-slate-500'}`}>
                        {tier.period}
                      </span>
                    </div>
                    <p className={`text-sm mb-5 leading-relaxed ${isPopular ? 'text-slate-300' : 'text-slate-500'}`}>
                      {tier.description}
                    </p>
                    <ul className="space-y-2.5 mb-6 flex-1">
                      {tier.features.map((feature, fIndex) => {
                        const isHeader = feature.endsWith(':');
                        return (
                          <li key={fIndex} className={`flex items-start gap-2.5 ${isHeader ? 'pt-1' : ''}`}>
                            {!isHeader && (
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isPopular ? 'bg-white/15' : 'bg-[#10B981]/10'}`}>
                                <Check className={`w-2.5 h-2.5 ${isPopular ? 'text-white' : 'text-[#10B981]'}`} />
                              </div>
                            )}
                            <span className={`text-sm ${isHeader ? 'font-semibold' : ''} ${isPopular ? 'text-slate-200' : 'text-slate-600'}`}>
                              {feature}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="flex flex-col gap-2 mt-auto">
                      <a href="mailto:sales@decoded.com?subject=Enterprise%20Demo%20Request">
                        <Button 
                          className={`w-full rounded-full py-5 font-semibold ${
                            isPopular 
                              ? 'bg-white text-slate-900 hover:bg-slate-100' 
                              : 'bg-slate-900 text-white hover:bg-slate-800'
                          }`}
                          data-testid={`enterprise-cta-demo-${index}`}
                        >
                          Request Demo
                        </Button>
                      </a>
                      <a href="mailto:sales@decoded.com?subject=Enterprise%20Sales%20Inquiry">
                        <Button 
                          variant="outline"
                          className={`w-full rounded-full py-5 font-semibold ${
                            isPopular 
                              ? 'border-white/30 text-white hover:bg-white/10' 
                              : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                          }`}
                          data-testid={`enterprise-cta-sales-${index}`}
                        >
                          Contact Sales
                        </Button>
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 bg-gradient-to-br from-[#2563EB] via-[#1D4ED8] to-[#1E40AF] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#06B6D4]/20 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white mb-6">
            Ready to optimize your import costs?
          </h2>
          <p className="text-lg text-blue-200 mb-8 max-w-2xl mx-auto">
            Join hundreds of importers who have discovered hidden savings in their product catalogs.
          </p>
          <Link to="/signup">
            <Button 
              className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-10 py-6 text-lg font-semibold shadow-xl hover:scale-[1.02] transition-all duration-300"
              data-testid="cta-get-started-btn"
            >
              Start Free Analysis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#06B6D4] flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl text-white">Decoded</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/resources" className="text-slate-400 hover:text-white transition-colors">
                Resources
              </Link>
              <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
            </div>
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Decoded. All rights reserved.
            </p>
          </div>
          <p className="text-slate-500 text-xs text-center mt-6">
            Optimization suggestions based on AI analysis. Not legal advice.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
